import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Briefcase, Users, Building2, TrendingUp, Target, Search, Activity, ChevronRight } from 'lucide-react';
import RecruiterLayout from '@/components/RecruiterLayout';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalCandidates: 0,
    totalClients: 0,
    totalCompanies: 0,
    availableCandidates: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [jobs, candidates, clients, companies, available] = await Promise.all([
        supabase.from('jobs').select('id, status', { count: 'exact' }),
        supabase.from('candidates').select('id', { count: 'exact' }),
        supabase.from('clients').select('id', { count: 'exact' }),
        supabase.from('companies').select('id', { count: 'exact' }),
        supabase.from('candidates').select('id', { count: 'exact' }).eq('availability_status', 'available')
      ]);

      setStats({
        totalJobs: jobs.count || 0,
        activeJobs: jobs.data?.filter(j => j.status === 'published').length || 0,
        totalCandidates: candidates.count || 0,
        totalClients: clients.count || 0,
        totalCompanies: companies.count || 0,
        availableCandidates: available.count || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const quickActions = [
    {
      title: 'Skill Intelligence',
      description: 'Find companies with specific skill expertise',
      icon: Target,
      href: '/intelligence/skills',
      color: 'text-blue-500'
    },
    {
      title: 'Product Search',
      description: 'Search candidates by product experience',
      icon: Search,
      href: '/intelligence/products',
      color: 'text-green-500'
    },
    {
      title: 'Talent Movement',
      description: 'Track market trends and talent flow',
      icon: Activity,
      href: '/intelligence/talent-movement',
      color: 'text-purple-500'
    },
    {
      title: 'Company Intel',
      description: 'Browse company profiles and analytics',
      icon: Building2,
      href: '/intelligence',
      color: 'text-orange-500'
    }
  ];

  return (
    <RecruiterLayout>
      <div>
        <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-muted-foreground mb-8">Welcome to your recruitment console</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Jobs</p>
                <p className="text-3xl font-bold">{stats.totalJobs}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats.activeJobs} active</p>
              </div>
              <Briefcase className="h-10 w-10 text-primary opacity-50" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Candidates</p>
                <p className="text-3xl font-bold">{stats.totalCandidates}</p>
                <p className="text-xs text-green-600 mt-1">{stats.availableCandidates} available</p>
              </div>
              <Users className="h-10 w-10 text-primary opacity-50" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Companies</p>
                <p className="text-3xl font-bold">{stats.totalCompanies}</p>
              </div>
              <Building2 className="h-10 w-10 text-primary opacity-50" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Clients</p>
                <p className="text-3xl font-bold">{stats.totalClients}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-primary opacity-50" />
            </div>
          </Card>
        </div>

        <h2 className="text-xl font-semibold mb-4">Intelligence & Analytics</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {quickActions.map(action => {
            const Icon = action.icon;
            return (
              <Link key={action.href} to={action.href}>
                <Card className="p-5 hover:shadow-lg transition-all hover:border-primary/50 cursor-pointer group h-full">
                  <div className="flex items-start justify-between mb-3">
                    <Icon className={`h-8 w-8 ${action.color}`} />
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">{action.title}</h3>
                  <p className="text-sm text-muted-foreground">{action.description}</p>
                </Card>
              </Link>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="font-semibold mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <Link to="/recruiter/jobs">
                <Button variant="outline" className="w-full justify-start">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Manage Jobs
                </Button>
              </Link>
              <Link to="/recruiter/candidates">
                <Button variant="outline" className="w-full justify-start">
                  <Users className="h-4 w-4 mr-2" />
                  View Candidates
                </Button>
              </Link>
              <Link to="/recruiter/clients">
                <Button variant="outline" className="w-full justify-start">
                  <Building2 className="h-4 w-4 mr-2" />
                  Client Management
                </Button>
              </Link>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold mb-3">Getting Started</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center">1</span>
                Add companies and clients to your database
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center">2</span>
                Create job listings and assign to clients
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center">3</span>
                Upload candidate resumes for AI parsing
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center">4</span>
                Use Intelligence module for market insights
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </RecruiterLayout>
  );
};

export default Dashboard;
